/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import { setModalType, openModal } from '../../../../redux/actions';
import { useGetSassTokenQuery } from '../../../../redux/apis/Sass.api';
import {
  useGetEmployeeConfigQuery,
  usePostRemoveEmployeeFromUserFavsMutation
} from '../../../../redux/apis/Users.api';
// import { openModal, setModalType } from '../../../redux/actions';

import { handleBlobPicUrl, userProfilePicUrl } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  AddEmployeesBtn,
  AddEmployeeButtonContainer,
  AdhocListContainer,
  EmployeeNameInTable,
  EmployeeSlot,
  EmployeeAvatar,
  ManagerNameInTable,
  Meta,
  RemoveButton,
  TextInTable
} from './AdhocListTable.styles';

/**
 * Static File Imports
 */
import defaultImage from '../../../../assets/images/defaultImage.png';

export interface MyEmployeesListProps {
  authorizedUser?: AuthUserProps;
}

const AdhocListTable: FC<MyEmployeesListProps> = ({ authorizedUser }) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('lastName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Employee');

  const {
    data: { favEmployees: favEmployeesList = [] } = {},
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(authorizedUser?.hcaid ?? '', {
    refetchOnMountOrArgChange: 120
  });
  const [
    removeEmployeeFromUserFavs
    // {
    //   isError: isRemoveEmployeeFromUserFavsError,
    //   isLoading: isRemoveEmployeeFromUserFavsLoading,
    //   isSuccess: isRemoveEmployeeFromUserFavsSuccess
    // }
  ] = usePostRemoveEmployeeFromUserFavsMutation();
  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const handleRemoveEmployee = async (employee: Employee) => {
    const payload = {
      userId: authorizedUser?.hcaid ?? '',
      employeeId: employee?.hcaid ?? ''
    };
    if (authorizedUser?.hcaid && employee?.hcaid) {
      await removeEmployeeFromUserFavs(payload);
    }
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'lastName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'Title':
        key = 'title';
        break;
      case 'Manager':
        key = 'managerFullName';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEditModal = () => {
    dispatch(setModalType('Add Employees'));
    dispatch(openModal());
  };

  const sortedList = useMemo(() => {
    const stateCopy = [...favEmployeesList];

    stateCopy.sort((a: Employee, b: Employee) =>
      a[sortKey as keyof Employee]?.toLowerCase() >
      b[sortKey as keyof Employee]?.toLowerCase()
        ? sortDir === 'asc'
          ? 1
          : -1
        : sortDir === 'asc'
        ? -1
        : 1
    );

    return stateCopy;
  }, [favEmployeesList, sortKey, sortDir]);

  const isTableEmpty = useMemo(() => sortedList.length === 0, [sortedList]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="Adhoc-List-Table-Employee-Column"
      unit="column"
      colWidth={22}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Department"
      id="Adhoc-List-Table-Department-Column"
      unit="column"
      colWidth={22}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Title"
      id="Adhoc-List-Table-Title-Column"
      unit="column"
      colWidth={22}
      direction={!(active === 'Title') ? 'none' : sortDir}
      sort
    >
      Title
    </CellContainer>,
    <CellContainer
      key="Manager"
      id="Adhoc-List-Table-Manager-Column"
      unit="column"
      colWidth={22}
      direction={!(active === 'Manager') ? 'none' : sortDir}
      sort
    >
      Manager
    </CellContainer>,
    <CellContainer
      key="Delete"
      id="Adhoc-List-Table-Delete-Column"
      unit="column"
      colWidth={12}
      direction="none"
    />
  ];

  const rows = sortedList.map((employee: Employee, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Adhoc-List-Row-${i}`}
        id={`Adhoc-List-Row-${employee.hcaid}`}
        unit="row"
        size="default"
        height="5em"
      >
        <CellContainer unit="column" colWidth={22}>
          <EmployeeSlot>
            {sasTokenData ? (
              <EmployeeAvatar
                src={
                  handleBlobPicUrl(
                    employee.pictureUrl,
                    sasTokenData.sasToken
                  ) ?? userProfilePicUrl(employee.hcaid, sasTokenData.sasToken)
                }
                alt="Employee Image"
                onError={e => {
                  e.currentTarget.src = defaultImage;
                  return null;
                }}
              />
            ) : (
              <EmployeeAvatar src={defaultImage} alt="Default Image" />
            )}
            <div>
              <EmployeeNameInTable className="my-0">{`${employee.firstName} ${employee.lastName}`}</EmployeeNameInTable>
              <TextInTable className="my-0">{employee.hcaid}</TextInTable>
            </div>
          </EmployeeSlot>
        </CellContainer>
        <CellContainer unit="column" colWidth={22}>
          <Meta>
            <TextInTable className="my-0">{employee.dept}</TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={22}>
          <Meta>
            <TextInTable className="my-0">{employee.title}</TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={22}>
          <Meta>
            <div>
              <ManagerNameInTable className="my-0">
                {employee.managerFullName || 'Unknown'}
              </ManagerNameInTable>
              <TextInTable className="my-0">
                {employee.managerHcaid}
              </TextInTable>
            </div>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={12}>
          <RemoveButton
            data-testid="remove-button"
            color="orange"
            kind="secondary"
            size="small"
            onClick={() => handleRemoveEmployee(employee)}
          >
            Remove
          </RemoveButton>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <AdhocListContainer
      id="Adhoc-List-Container"
      className="d-flex flex-column w-100"
    >
      <AddEmployeeButtonContainer>
        <div className="d-flex align-items-center">
          <AddEmployeesBtn
            id="Add-Employees-Button"
            data-testid="add-employees-button"
            color="blue"
            kind="primary"
            size="small"
            style={{ fontSize: '1rem', marginRight: '10px' }}
            onClick={openEditModal}
          >
            Add Employees
          </AddEmployeesBtn>
        </div>
      </AddEmployeeButtonContainer>
      <Table
        customClass="my-adhoc-list-table"
        headers={headers}
        rows={rows}
        isTableEmpty={isTableEmpty}
        loading={isGetEmployeeConfigLoading || isGetSassTokenFetching}
        type="adhoc-list"
        onSort={(e: any) => handleSort(e.detail.label)}
      />
    </AdhocListContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};

export default connect(mapReduxStateToProps)(AdhocListTable);
